// Ported from LC2's DaysOnMarket.js + DaysOnMarketInfo.js (commons/components/HomeTabs/
// Performance/Metrics/) — same `getDaysOnMarketByCommunitySfid` fetch, same visibility gate
// (community.hidden_loyalty_cloud_features__c toggled by the admin-only VisibilitySwitch,
// always visible for a Stake admin), same is_loading/is_visible/is_supported state machine.
// SIMPLIFIED: LC2 rendered the comps + main tables with MUI X DataGridPro (a licensed
// package this app doesn't depend on — see StyledTable/DaysOnMarketInfo.js). The API already
// returns pre-formatted display values per cell (`row.format` for chip styling, `row.tooltip`
// for the info-icon tooltip — see LC2's TableBodyCell.renderColumnCell) rather than raw
// numbers the client formats, so a plain MUI Table reproduces the same rendering; only the
// DataGrid chrome (column resize, virtualization) is dropped. The `graph_data` chart is
// rendered with ./MetricsBarChart.js (plain CSS) instead of recharts — see that file's
// header comment.
import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {Box, ButtonBase, Chip, Collapse, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, useTheme} from '@mui/material';
import {InfoOutlined} from '@mui/icons-material';

import {tokens} from '../../../themes/ThemeCustomization';
import {getIsStakeAdmin} from '../../../app/store/reducers/session';
import {getDashboardAccount, getDashboardCommunity} from '../../../app/store/reducers/dashboard';
import {getCommunities} from '../../../app/store/reducers/entities';
import {getOpenItem} from '../../../app/store/reducers/menu';
import {useGetDaysOnMarketByCommunitySfidQuery} from '../../../features/coreApi/metricsSlice';
import LoadingBox from '../../../commons/components/LoadingBox';
import VisibilitySwitch from './VisibilitySwitch';
import MetricsBarChart from './MetricsBarChart';

// Ported from LC2's TableBodyCell.renderColumnCell: cell values arrive pre-formatted from
// the API; `row.format` optionally requests a colored chip, `row.tooltip[field]` optionally
// adds an info tooltip. Only the FontAwesome icon swap (-> MUI InfoOutlined) is new.
const GenericCell = ({field, row}) => {
    const value = row[field];
    const format = row?.format?.item || row?.format;
    const tooltip = row?.tooltip?.[field];

    let content;
    if (format === 'chip' && value !== '-') {
        content = (
            <Chip
                label={value}
                size="small"
                sx={{backgroundColor: row.format?.color || row?.backgroundTextColor, color: row.format?.text || row?.textColor}}
            />
        );
    } else {
        content = (
            <Typography variant="body2" sx={{fontWeight: row?.fontWeight}}>
                {value}
            </Typography>
        );
    }

    if (!tooltip) return content;

    return (
        <Stack direction="row" alignItems="center" spacing={0.5}>
            {content}
            <Tooltip title={tooltip.text || tooltip.label || ''} arrow>
                <InfoOutlined sx={{fontSize: 14, color: tokens.color.neutral400}} />
            </Tooltip>
        </Stack>
    );
};

GenericCell.propTypes = {field: PropTypes.string, row: PropTypes.object};

const GenericTable = ({columns = [], rows = []}) => {
    const theme = useTheme();
    return (
        <Table size="small" sx={{border: `1px solid ${theme.palette.grey[300]}`, borderRadius: '8px'}}>
            <TableHead>
                <TableRow sx={{backgroundColor: theme.palette.grey[100]}}>
                    {columns.map((col) => (
                        <TableCell key={col.field} sx={{fontWeight: 500}}>
                            {col.headerName}
                        </TableCell>
                    ))}
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row, idx) => (
                    <TableRow key={row.id ?? idx}>
                        {columns.map((col) => (
                            <TableCell key={col.field}>
                                <GenericCell field={col.field} row={row} />
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
                {rows.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={columns.length} sx={{textAlign: 'center', color: 'text.secondary'}}>
                            No data
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    );
};

GenericTable.propTypes = {columns: PropTypes.array, rows: PropTypes.array};

const DaysOnMarketHeader = ({data = {}}) => (
    <Stack direction="column">
        <Typography variant="h5" fontWeight="fontWeightMedium">
            {data.title}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" fontWeight="fontWeightRegular">
            {data.description}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" fontWeight="fontWeightRegular">
            This data is sourced from publicly available online listings.
        </Typography>
    </Stack>
);

DaysOnMarketHeader.propTypes = {data: PropTypes.object};

const DaysOnMarketCard = ({data = {}}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const {description, table_data = {}} = data;

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle2" color="text.secondary" fontWeight="fontWeightRegular">
                    {description}
                </Typography>
                <ButtonBase disableRipple onClick={() => setIsExpanded((v) => !v)}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{textDecoration: 'underline'}}>
                        {isExpanded ? 'Hide comps' : 'Show comps'}
                    </Typography>
                </ButtonBase>
            </Stack>
            <Collapse in={isExpanded}>
                <Box sx={{pt: 1}}>
                    <GenericTable columns={table_data.columns} rows={table_data.rows} />
                </Box>
            </Collapse>
        </Box>
    );
};

DaysOnMarketCard.propTypes = {data: PropTypes.object};

const DaysOnMarketSummary = ({data = []}) => (
    <Stack direction="row" columnGap={1}>
        {data.map((item) => (
            <Box key={item.title} sx={{width: '100%', borderRadius: '8px', border: `1px solid ${tokens.color.neutral200}`, p: 2}}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="fontWeightRegular">
                        {item.title}
                    </Typography>
                    <Chip label={item.value} sx={{backgroundColor: item.backgroundColor, color: item.fontColor}} />
                </Stack>
            </Box>
        ))}
    </Stack>
);

DaysOnMarketSummary.propTypes = {data: PropTypes.array};

const DaysOnMarketTable = ({data = {}}) => <GenericTable columns={data.columns} rows={data.rows} />;

DaysOnMarketTable.propTypes = {data: PropTypes.object};

const DaysOnMarket = () => {
    const params = useParams();
    const openItem = useSelector(getOpenItem);
    const communities = useSelector(getCommunities);
    const isStakeAdmin = useSelector(getIsStakeAdmin);
    const dashboardAccount = useSelector(getDashboardAccount);
    const dashboardCommunity = useSelector(getDashboardCommunity);

    const community = communities.find((c) => c.sfid === dashboardCommunity.sfid);

    const {data, isLoading, isSuccess, isFetching, isError} = useGetDaysOnMarketByCommunitySfidQuery(params?.community_sfid, {
        skip: !params?.community_sfid
    });
    const [isVisible, setVisible] = useState(false);

    useEffect(() => {
        if (openItem === 'communities' && community) {
            setVisible(!community?.hidden_loyalty_cloud_features__c?.includes('days_on_market'));
        }
    }, [community, openItem]);

    const daysOnMarketData = useMemo(() => {
        const defaultResponse = {is_visible: false, is_loading: false};

        if (!isVisible && !isStakeAdmin) return defaultResponse;
        if (isFetching || isLoading) return {is_loading: true};
        if (isSuccess && (isVisible || isStakeAdmin)) {
            return data?.is_supported ? {...data, is_visible: true, is_loading: false} : defaultResponse;
        }
        return defaultResponse;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isVisible, isStakeAdmin, isSuccess, isLoading, isFetching, JSON.stringify(data)]);

    if (daysOnMarketData?.is_loading) {
        return (
            <Box sx={{p: 2, width: '100%', border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.card, backgroundColor: tokens.color.white}}>
                <LoadingBox />
            </Box>
        );
    }

    if (!daysOnMarketData?.is_visible) {
        if (isError) {
            return (
                <Box sx={{p: 2, width: '100%', border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.card, backgroundColor: tokens.color.white}}>
                    <Typography variant="body2" color="error.main" sx={{textAlign: 'center', py: 4}}>
                        Failed to fetch days on market data.
                    </Typography>
                </Box>
            );
        }
        return null;
    }

    const {header, comps, summary, table_data, graph_data} = data;

    return (
        <Box sx={{p: 2, width: '100%', border: `1px solid ${tokens.color.neutral200}`, borderRadius: tokens.radius.card, backgroundColor: tokens.color.white}}>
            <Stack direction="column" rowGap={2} divider={<Box sx={{borderTop: `1px solid ${tokens.color.neutral200}`}} />}>
                <DaysOnMarketHeader data={header} />
                <DaysOnMarketCard data={comps} />
                <DaysOnMarketSummary data={summary} />
                <MetricsBarChart graphData={graph_data} name="Days on Market" />
                <DaysOnMarketTable data={table_data} />
                {isStakeAdmin && (
                    <VisibilitySwitch
                        id={dashboardCommunity?.sfid}
                        accountName={dashboardAccount?.name}
                        setVisible={setVisible}
                        isVisible={isVisible}
                        modelType="campaign"
                        featureType="days_on_market"
                    />
                )}
            </Stack>
        </Box>
    );
};

export default DaysOnMarket;
